/**
 * GrowthProducts — Top products ranking with horizontal bars (premium design)
 */
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

export default function GrowthProducts({ authAxios, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await authAxios.get(`/growth/products?days=${days}&limit=10`);
        setData(res.data);
      } catch (err) {
        console.error("Error fetching products:", err);
        toast.error("Erro ao carregar produtos");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [days, authAxios]);

  if (loading) {
    return (
      <div className="grid lg:grid-cols-2 gap-6">
        {Array(2).fill(0).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Array(5).fill(0).map((_, j) => (
                  <Skeleton key={j} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Sem dados de produtos</p>
        </CardContent>
      </Card>
    );
  }

  const { most_sold, highest_revenue } = data;

  const ProductRanking = ({ products, title, metric, icon: Icon, color }) => {
    const maxValue = Math.max(...products.slice(0, 10).map(p => metric === "quantity" ? p.quantity : p.revenue));

    return (
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                {Icon && <Icon className={`w-5 h-5 ${color}`} />}
                {title}
              </CardTitle>
              <p className="text-xs text-slate-600 mt-1">Top 10 produtos</p>
            </div>
            {Icon && <Trophy className={`w-6 h-6 ${color} opacity-20`} />}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {products.slice(0, 10).map((p, idx) => (
              <div key={idx} className="group">
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={`text-sm font-bold w-6 flex-shrink-0 ${
                      idx === 0 ? "text-yellow-600" : idx === 1 ? "text-gray-400" : idx === 2 ? "text-orange-600" : "text-slate-400"
                    }`}>
                      #{idx + 1}
                    </span>
                    <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-slate-700">
                      {p.product_name || "Produto desconhecido"}
                    </p>
                  </div>
                  <p className="text-sm font-bold text-slate-900 flex-shrink-0 ml-2">
                    {metric === "quantity" ? `${p.quantity} un.` : `€${p.revenue.toFixed(2)}`}
                  </p>
                </div>
                {/* Horizontal Bar */}
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${color}`}
                    style={{
                      width: `${(metric === "quantity" ? p.quantity : p.revenue) / maxValue * 100}%`
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  €{p.revenue.toFixed(2)} • {p.quantity} unidades
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Top 10 Produtos</p>
              <p className="text-xs text-slate-600">Últimos {days} dias</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ranking Tables */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ProductRanking
          products={most_sold}
          title="Mais Vendidos"
          metric="quantity"
          icon={ShoppingCart}
          color="bg-green-500"
        />
        <ProductRanking
          products={highest_revenue}
          title="Maior Faturamento"
          metric="revenue"
          icon={Trophy}
          color="bg-yellow-500"
        />
      </div>
    </div>
  );
}
